import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUpRight,
  TrendingUp,
  LineChart,
  ShieldCheck,
  MapPin,
} from "lucide-react";
import { postInvestmentInquiry } from "@/lib/api";

const SHEEP_IMG =
  "https://images.unsplash.com/photo-1517347723389-1983cac7e239?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxzaGVlcCUyMGZsb2NrJTIwZ3JhemluZ3xlbnwwfHx8fDE3ODMwOTE0MTJ8MA&ixlib=rb-4.1.0&q=85";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    min: "₹50,000",
    heads: "5 heads",
    cycle: "6 months",
    irr: "16-18%",
    perks: ["Monthly video reports", "Basic dashboard", "Insurance covered"],
  },
  {
    id: "grower",
    name: "Grower",
    min: "₹2,00,000",
    heads: "22 heads",
    cycle: "6 months",
    irr: "18-22%",
    perks: [
      "Bi-weekly video reports",
      "Advanced dashboard",
      "Farm visits (2/yr)",
      "Priority allocation",
    ],
    featured: true,
  },
  {
    id: "flock",
    name: "Flock",
    min: "₹10,00,000+",
    heads: "100+ heads",
    cycle: "12 months",
    irr: "20-24%",
    perks: [
      "Dedicated ops liaison",
      "Custom flock strategy",
      "Quarterly on-site audits",
      "Priority exit windows",
    ],
  },
];

const HOW = [
  { i: MapPin, t: "You pick a flock", d: "Choose from active flocks with named farms and health data." },
  { i: LineChart, t: "We manage on-ground", d: "Our operations team handles feed, vet, insurance and sale." },
  { i: TrendingUp, t: "You track & earn", d: "Watch growth in your dashboard. Yield on exit." },
];

export default function SheepInvest() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    investment_amount: "",
    preferred_plan: "starter",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postInvestmentInquiry({
        ...form,
        investment_amount: parseFloat(form.investment_amount),
      });
      toast.success("Inquiry received.", {
        description: "Our investment team will reach out within 24 hours.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        investment_amount: "",
        preferred_plan: "starter",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        "Please check your details and try again.";
      toast.error("Couldn’t send inquiry", { description: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="flockfolio-page">
      {/* Hero */}
      <section className="bg-moss text-bone border-b border-moss-deep relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] grid-lines pointer-events-none" />
        <div className="container-x py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end relative">
          <div className="lg:col-span-7">
            <div className="overline text-bone/70">/ Product · Flockfolio</div>
            <h1
              data-testid="flockfolio-heading"
              className="mt-6 font-display font-black text-[clamp(3rem,7vw,6.5rem)] leading-[0.9] tracking-tighter"
            >
              Flockfolio.
            </h1>
            <div className="mt-2 font-serif italic text-2xl sm:text-3xl text-bone/80">
              Invest in sheep. Track growth. Share the yield.
            </div>
            <p className="mt-8 max-w-2xl text-lg text-bone/80 leading-relaxed">
              Flockfolio is our transparent, on-ground sheep-flock investment
              platform. Real livestock, real farms, real returns — audited by
              our operations team and surfaced through a clean investor
              dashboard powered by{" "}
              <a
                href="https://passiveiearn.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-gold-soft underline-offset-2 hover:text-bone"
              >
                passiveiearn.com
              </a>
              .
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="https://passiveiearn.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-bone text-moss px-6 py-3 font-sans font-medium hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(247,245,240,0.25)] transition-all duration-300"
                data-testid="flockfolio-view-plans"
              >
                Go to passiveiearn.com <ArrowUpRight size={18} />
              </a>
              <a
                href="#inquire"
                className="inline-flex items-center gap-2 border border-bone/40 text-bone px-6 py-3 font-sans font-medium hover:bg-bone hover:text-moss transition-colors"
                data-testid="flockfolio-inquire-cta"
              >
                Talk to us <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[5/6] overflow-hidden border border-bone/30">
              <img
                src={SHEEP_IMG}
                alt="Sheep flock grazing"
                className="w-full h-full object-cover"
                data-testid="flockfolio-hero-image"
              />
              <div className="absolute top-4 left-4 mono-label bg-bone text-moss px-2 py-1">
                / Live · Telangana Belt
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-bone-300">
        <div className="container-x py-24 sm:py-32">
          <div className="overline">/ How it works</div>
          <h2 className="mt-4 font-serif italic font-light text-4xl sm:text-5xl text-ink-olive">
            Fractional livestock, done honestly.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {HOW.map((h, i) => {
              const Icon = h.i;
              return (
                <div
                  key={h.t}
                  className="border border-bone-300 bg-white p-8"
                  data-testid={`flockfolio-how-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex w-11 h-11 items-center justify-center bg-moss/10 text-moss">
                      <Icon size={20} />
                    </span>
                    <span className="mono-label text-moss">/ 0{i + 1}</span>
                  </div>
                  <div className="mt-8 font-display font-bold text-2xl text-ink-olive">
                    {h.t}
                  </div>
                  <p className="mt-2 text-ink-soft leading-relaxed">{h.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="border-b border-bone-300 bg-white">
        <div className="container-x py-24 sm:py-32">
          <div className="overline">/ Plans</div>
          <h2 className="mt-4 font-serif italic font-light text-4xl sm:text-5xl text-ink-olive">
            Pick a size that fits your appetite.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {PLANS.map((p, i) => (
              <div
                key={p.id}
                data-testid={`flockfolio-plan-${p.id}`}
                className={`border p-8 flex flex-col ${
                  p.featured
                    ? "border-moss bg-moss text-bone"
                    : "border-bone-300 bg-bone-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div
                    className={`mono-label ${
                      p.featured ? "text-bone/70" : "text-moss"
                    }`}
                  >
                    / {p.name}
                  </div>
                  {p.featured && (
                    <div className="mono-label bg-bone text-moss px-2 py-1">
                      Popular
                    </div>
                  )}
                </div>
                <div
                  className={`mt-6 font-display font-black text-4xl tracking-tighter ${
                    p.featured ? "text-bone" : "text-ink-olive"
                  }`}
                >
                  {p.min}
                </div>
                <div
                  className={`mt-1 text-sm ${
                    p.featured ? "text-bone/70" : "text-ink-soft"
                  }`}
                >
                  {p.heads} · {p.cycle} · Target IRR {p.irr}
                </div>
                <ul className="mt-8 space-y-3 flex-1">
                  {p.perks.map((perk) => (
                    <li
                      key={perk}
                      className={`flex items-start gap-2 text-sm ${
                        p.featured ? "text-bone/90" : "text-ink-olive"
                      }`}
                    >
                      <ShieldCheck
                        size={16}
                        className={`mt-0.5 ${
                          p.featured ? "text-bone" : "text-moss"
                        }`}
                      />
                      {perk}
                    </li>
                  ))}
                </ul>
                <a
                  href={`https://passiveiearn.com?plan=${p.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`flockfolio-plan-cta-${p.id}`}
                  className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3 font-sans font-medium transition-colors ${
                    p.featured
                      ? "bg-bone text-moss hover:bg-ink-olive hover:text-bone"
                      : "bg-moss text-bone hover:bg-moss-deep"
                  }`}
                >
                  Choose {p.name} <ArrowUpRight size={16} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquire */}
      <section
        id="inquire"
        className="border-b border-bone-300 bg-bone-50"
        data-testid="flockfolio-inquire-section"
      >
        <div className="container-x py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">/ Inquire</div>
            <h2 className="mt-4 font-serif italic font-light text-4xl sm:text-5xl text-ink-olive">
              Talk to our investment team.
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed">
              Share a rough amount and preferred cycle. We’ll come back with an
              allocation window, farm details and your onboarding checklist.
            </p>
          </div>
          <form
            onSubmit={submit}
            className="lg:col-span-7 border border-bone-300 bg-white p-8 sm:p-10 space-y-6"
            data-testid="flockfolio-inquire-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field
                label="Full name"
                testid="flockfolio-inquire-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                required
              />
              <Field
                label="Email"
                type="email"
                testid="flockfolio-inquire-email"
                value={form.email}
                onChange={(v) => update("email", v)}
                required
              />
              <Field
                label="Phone"
                testid="flockfolio-inquire-phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
              />
              <Field
                label="Amount (₹)"
                type="number"
                testid="flockfolio-inquire-amount"
                value={form.investment_amount}
                onChange={(v) => update("investment_amount", v)}
                required
                min="10000"
              />
            </div>
            <div>
              <label className="mono-label text-ink-soft">/ Preferred plan</label>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {PLANS.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => update("preferred_plan", p.id)}
                    data-testid={`flockfolio-inquire-plan-${p.id}`}
                    className={`px-3 py-3 font-sans text-sm border transition-colors ${
                      form.preferred_plan === p.id
                        ? "bg-moss text-bone border-moss"
                        : "bg-white text-ink-olive border-bone-300 hover:border-moss"
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="mono-label text-ink-soft">/ Anything else?</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                data-testid="flockfolio-inquire-message"
                className="mt-3 w-full border border-bone-300 bg-white px-4 py-3 font-sans text-ink-olive focus:border-moss focus:outline-none"
                placeholder="Timeline, questions, expectations..."
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="flockfolio-inquire-submit"
              className="inline-flex items-center gap-2 bg-moss text-bone px-6 py-3 font-sans font-medium hover:bg-moss-deep transition-colors disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send inquiry"} <ArrowUpRight size={18} />
            </button>
          </form>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink-olive text-bone">
        <div className="container-x py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <h3 className="lg:col-span-8 font-display font-black text-4xl sm:text-5xl tracking-tighter">
            Prefer a general chat first?
          </h3>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/contact"
              data-testid="flockfolio-general-contact"
              className="inline-flex items-center gap-2 bg-bone text-ink-olive px-6 py-3 font-sans font-medium hover:bg-moss hover:text-bone transition-colors"
            >
              Contact us <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", ...rest }) {
  return (
    <div>
      <label className="mono-label text-ink-soft">/ {label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-3 w-full border border-bone-300 bg-white px-4 py-3 font-sans text-ink-olive focus:border-moss focus:outline-none"
        {...rest}
      />
    </div>
  );
}
